import PropTypes from 'prop-types';
import React, { Component } from 'react';
import _ from 'lodash';
import pluralize from 'pluralize';
import { withRouter } from 'react-router-dom';

import { algoliaEntitiesByIndex } from 'config/constants';
import { getTrackUrlFilters } from 'helpers';

import ActionBar from 'components/ActionBar';
import AlgoliaPagination from 'components/AlgoliaPagination';
import Well from 'components/Well';
import WindowAutoScroller from 'components/WindowAutoScroller';
import UrlTableFilters from './UrlTableFilters';
import { TABLE_INPUT_SELECTOR } from './helpers';

/**
 * [TableWithSearch]
 * Show table of data with pagination.  Uses facetFilters prop to determine what data to filter.
 * If entry in search input, will search data and display search results in place of original filtered data
 * Both search results and facetFilters results have their own pagination.
 *
 * @prop {array} facetFilters - filter index by facet values. Same as algolia docs
 * @prop {string[]} filters
 * @prop {number} [limit] - results per page. Defaults to 100
 * https://www.algolia.com/doc/api-reference/api-parameters/facetFilters/
 * @prop {bool} urlFilters OPTIONAL - if passed, will look for supported facet filters in the url query params
 * and apply them to the search arguments, as well as display filter details alongside action bar
 * @prop {string} placeholderText - placeholder text for search input
 * @prop {string} limitReachedText - text displayed when end of results reached
 * @prop {node || func} [renderTable] render prop for rendering a react-virtualized table component
 * @prop {node || func} [renderList] render prop for rendering a non-windowed list.
 * @prop {node || func} renderLoading
 * @prop {bool} forceEmpty OPTIONAL - if passed will force return of renderEmpty prop
 * @prop {node || func} renderEmpty - Rendered when no data for provided entity.
 * Not displayed when search results within table come back empty
 * @prop {func} index - algolia search index function that should return a promise
 */

class TableWithSearch extends Component {
  constructor() {
    super();

    this._searchForData = _.debounce(this._searchForData, 300);
  }

  state = {
    searchText: '', // search input
    searchQuery: '' // debounce search input text that drives algolia pagination search
  };

  _handleSearchChange = e => {
    const searchText = e.target.value;

    // if we clear the search text we need to prevent renderEmpty from displaying temporarily
    // while we wait to trigger loading in debounced function
    if (searchText === '') {
      this.setState({ searchText, searchQuery: '' });
    } else {
      this.setState({ searchText });
      this._searchForData();
    }
  };

  _handleNoData = () => {
    const { searchText } = this.state;
    const entityName = this._getEntityName();

    if (searchText) {
      return `None of the ${pluralize(entityName.toLowerCase())} matched your search.`;
    }

    return `No ${pluralize(entityName.toLowerCase())} found.`;
  };

  _searchForData = () => {
    const { searchText } = this.state;
    this.setState({ searchQuery: searchText });
  };

  _getEntityName = () => {
    const { index } = this.props;
    return algoliaEntitiesByIndex[index];
  };

  render() {
    const {
      limitReachedText,
      placeholderText,
      facetFilters = [],
      facets,
      filters,
      restrictSearchableAttributes,
      urlFilters,
      renderTable,
      renderList,
      renderLoading = () => null,
      renderAside,
      renderActionBarActions,
      renderEmpty,
      index,
      location,
      limit,
      forceEmpty,
      ...rest
    } = this.props;

    const { searchText, searchQuery } = this.state;
    const entityName = this._getEntityName();

    // if urlFilters prop grab any filters from url and add to facetFilters
    if (urlFilters) {
      const { labelIds } = getTrackUrlFilters(location);
      const urlFacetFilters = labelIds.map(labelId => `label.id:${labelId}`);
      facetFilters.push(urlFacetFilters);
    }

    return (
      <AlgoliaPagination
        infiniteScroll
        searchParams={{
          index,
          facetFilters,
          facets,
          filters,
          query: searchQuery,
          restrictSearchableAttributes,
          limit
        }}
        cacheName={searchQuery ? 'search' : 'filter'}
      >
        {algoliaProps => {
          const { data, loading, limitReached } = algoliaProps;

          const noData = data.length === 0 && !loading;
          if ((noData && renderEmpty && !searchQuery) || forceEmpty) {
            return renderEmpty();
          }

          return (
            <div style={{ width: '100%' }}>
              <ActionBar
                searchInput={{
                  onChange: this._handleSearchChange,
                  placeholder: placeholderText || `Search ${pluralize(entityName)}`,
                  value: searchText,
                  'data-testid': TABLE_INPUT_SELECTOR
                }}
                renderAside={urlFilters ? () => <UrlTableFilters /> : renderAside}
                renderActions={renderActionBarActions}
              />

              {noData ? (
                <div className="col-xs-12">
                  <Well>{this._handleNoData(data)}</Well>
                </div>
              ) : (
                <>
                  {renderTable && (
                    <WindowAutoScroller>
                      {scrollProps =>
                        renderTable({
                          ...algoliaProps,
                          // scrollProps must be used with React Virtualized table / list
                          windowScroller: scrollProps,
                          ...rest
                        })
                      }
                    </WindowAutoScroller>
                  )}

                  {renderList &&
                    renderList({
                      ...algoliaProps,
                      ...rest
                    })}

                  {!limitReached && data.length >= 10 && renderLoading()}

                  {limitReached && data.length > 1 && (
                    <div className="Table__footer-message">
                      Viewing {data.length} {pluralize(entityName)}{' '}
                      {limitReachedText || null}
                    </div>
                  )}

                  {limitReached && data.length === 1 && (
                    <div className="Table__footer-message" />
                  )}
                </>
              )}
            </div>
          );
        }}
      </AlgoliaPagination>
    );
  }
}

TableWithSearch.propTypes = {
  // from container
  facets: PropTypes.arrayOf(PropTypes.string),
  facetFilters: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.array])
  ),
  filters: PropTypes.string,
  urlFilters: PropTypes.bool,
  entityName: PropTypes.string,
  index: PropTypes.string,
  placeholderText: PropTypes.string,
  limitReachedText: PropTypes.string,
  renderTable: PropTypes.func,
  renderList: PropTypes.func,
  renderLoading: PropTypes.func,
  renderEmpty: PropTypes.func,
  forceEmpty: PropTypes.bool,
  restrictSearchableAttributes: PropTypes.arrayOf(PropTypes.string),
  renderActionBarActions: PropTypes.func,
  limit: PropTypes.number,
  // withRouter
  location: PropTypes.shape()
};

export default withRouter(TableWithSearch);
